import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import { PrizetImageUpload } from '../PrizetImageUpload';
import { PrizetAttributeSelector } from '../PrizetAttributeSelector';
import { PrizetImageAPI } from '../../interface/interface';
import { PrizetS3Upload } from '../PrizetS3Upload';
import { LinkItem } from '../../hooks/useUpload';
import { useAuth } from 'src/hooks/useAuth';
import toast from 'react-hot-toast';
import { minWidth } from '@mui/system';

export interface DropdownOptionsProps {
  value: string;
  label: string;
}

interface HttpFieldsProps {
  type:
    | 'text'
    | 'select'
    | 'checkbox'
    | 'password'
    | 'email'
    | 'number'
    | 'tel'
    | 'url'
    | 'search'
    | 'date'
    | 'time'
    | 'datetime-local'
    | 'month'
    | 'week'
    | 'textarea'
    | 'tag'
    | 'color'
    | 'selector'
    | 'image'
    | 'heading'
    | 'spacer'
    | 's3'
    | 'hidden'
    | 'productAttributes';
  label?: string;
  placeholder?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  keyName?: string;
  payloadName?: string;
  selectOptions?: [DropdownOptionsProps];
  onChange?: (e: any, additionalParam?: any) => void;
  onImageUploadCallback?: (payload: PrizetImageAPI[]) => void;
  onS3UploadCallback?: (payload: LinkItem[]) => void;
  value: any;
  tagOptions?: any[];
  tagValue?: any[];
  children?: HttpFieldsProps[];
  onAttributeEvent?: (event: any) => void;
  options?: any;
  tagLabel?: string;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  selectedAttributes?: any[];
  attributes?: any[];
}

interface HttpLayoutProps {
  type: 'row' | 'column';
  children: HttpFieldsProps[] | any[];
}

interface HttpProps {
  fields: HttpFieldsProps[] | any[];
  baseUrl: string;
  additionalPayload?: Record<string, any>;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers: any;
  openModal: boolean;
  onClose: () => void;
  title: string;
  onErrorCallback: (error) => void;
  onSubmitCallback: (response: Response) => void;
  submitText?: string;
}

export const HttpModal = (props: HttpProps) => {
  const { enhancedFetch } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    onSubmitCallback,
    onErrorCallback,
    title,
    onClose,
    fields,
    baseUrl,
    method,
    headers,
    openModal,
    submitText
  } = props;

  // Helper function to transform field values based on field type
  const transformFieldValue = (name: string, value: any) => {
    if (value === undefined || value === null) return value;

    switch (name) {
      case 'additionalInfo':
        // Convert string to array for additionalInfo
        return typeof value === 'string'
          ? value.split('\n').filter((item) => item.trim())
          : Array.isArray(value)
          ? value
          : [];

      case 'attributes':
        if (!Array.isArray(value)) {
          console.warn('Attributes value is not an array:', value);
          return [];
        }

        // Flatten the attribute structure to match the desired API format
        return value.flatMap((attr) => {
          // Skip if missing attributeId
          if (!attr?.attributeId) {
            console.warn('Invalid attribute object missing attributeId:', attr);
            return [];
          }

          // Skip if values is not an array
          if (!Array.isArray(attr.values)) {
            console.warn(
              'Attribute values is not an array for attribute:',
              attr.attributeId
            );
            return [];
          }

          // Map each value to the desired format
          return attr.values
            .map((val) => {
              // Extract the value ID, handling different possible formats
              const valueId =
                typeof val === 'object' ? val.id || val.value : val;

              if (!valueId) {
                console.warn('Attribute value missing id/value:', val);
                return null;
              }

              return {
                productAttributeId: attr.attributeId,
                productAttributeValue: valueId
              };
            })
            .filter(Boolean); // Remove null entries
        });

      case 'productAttributes':
        // Transform productAttributes to match the schema's attributes format
        return value.map((attr) => ({
          attributeId: attr.attributeId || attr.productAttributeId,
          values: attr.values || [attr.productAttributeValueId || attr.value]
        }));

      // case 'states':
      //   if (!Array.isArray(value)) return [];

      //   return value.map((state) => {
      //     // Handle both state objects and state IDs
      //     if (typeof state === 'object' && state !== null) {
      //       return state.value || state.id;
      //     }
      //     return state;
      //   });
      case 'states':
        if (!Array.isArray(value)) return [];

        // Always return array of IDs for consistency
        return value
          .filter((state) => state !== null && state !== undefined)
          .map((state) => {
            if (typeof state === 'object' && state !== null) {
              return state.value || state.id; // Extract ID from object
            }
            return state; // Already an ID
          });

      case 'tags':
        if (!Array.isArray(value)) return [];
        return value.map((tag) => {
          if (typeof tag === 'string') {
            return { tagName: tag };
          }
          return typeof tag === 'object' && tag !== null
            ? tag.tagName
              ? tag
              : { tagName: tag.label || tag.name || tag.value }
            : { tagName: tag };
        });

      case 'productTags':
        return value.map((tag) => tag.id);

      case 'images':
        if (!Array.isArray(value)) return [];

        return value.map((img) => ({
          image: img.image || img.url,
          isThumbnail: img.isThumbnail || false
        }));

      case 'buyingPrice':
        return typeof value === 'string' ? parseFloat(value) : value;

      case 'availableUnits':
      case 'quantity':
        return typeof value === 'string' ? parseInt(value, 10) : value;

      default:
        return value;
    }
  };

  // Function to collect all field values recursively
  const collectFieldValues = (fieldList: any[]) => {
    let payload: Record<string, any> = {};

    fieldList.forEach((field) => {
      if (field.children) {
        // Handle nested fields (rows/columns)
        const childValues = collectFieldValues(field.children);
        payload = { ...payload, ...childValues };
      } else if (
        field.name &&
        field.type !== 'heading' &&
        field.type !== 'spacer'
      ) {
        // Get field value and transform it
        let fieldValue;
        if (field.type === 'tag' && field.tagValue !== undefined) {
          fieldValue = field.tagValue;
        } else {
          fieldValue = field.value;
        }

        // Special case

        if (fieldValue !== undefined) {
          const computedName = field.keyName ? field.payloadName : field.name;
          payload[computedName] = transformFieldValue(
            field.keyName ? field.keyName : field.name,
            fieldValue
          );
        }
      }
    });

    return payload;
  };

  const getFieldFromType = (
    field: HttpFieldsProps | HttpLayoutProps,
    index: number
  ): React.ReactNode=> {
    switch (field.type) {
      case 'image':
        return (
          <Box key={index}>
            <PrizetImageUpload
              value={field.value as unknown as any[]}
              onImageUploadCallback={field.onImageUploadCallback}
            />
          </Box>
        );
      case 's3':
        return (
          <Box key={index}>
            <PrizetS3Upload
              links={field.value as unknown as LinkItem[]}
              onS3UploadCallback={field.onS3UploadCallback}
            />
          </Box>
        );
      case 'tag':
        return (
          <Box key={index} sx={{ minWidth: 300 }}>
            <Autocomplete
              multiple
              id="tags-standard"
              options={field.tagOptions}
              getOptionLabel={(option) => option[field.tagLabel]}
              onChange={field.onChange}
              value={field.tagValue || field.value || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label={field.label}
                  placeholder={field.placeholder}
                />
              )}
            />
          </Box>
        );
      case 'hidden':
        return (
          <TextField
            key={index}
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            name={field.name}
            required={field.required}
            disabled={field.disabled}
            onChange={field.onChange}
            value={field.value}
            style={{ display: 'none' }}
          />
        );
      case 'row':
        return (
          <Box
            key={index}
            display="flex"
            maxWidth={'100%'}
            flexDirection="row"
            justifyContent={'space-between'}
            columnGap={2}
            marginBottom={'3%'}
            alignItems="flex-start"
          >
            {field.children.map((child, index) =>
              getFieldFromType(child, index)
            )}
          </Box>
        );
      case 'heading':
        return (
          <Typography key={index} variant={field.variant} gutterBottom>
            {field.label}
          </Typography>
        );
      case 'column':
        return (
          <Box
            key={index}
            display="flex"
            flexDirection="column"
            rowGap={2}
            alignItems="flex-start"
          >
            {field.children.map((child, index) => {
              return getFieldFromType(child, index);
            })}
          </Box>
        );
      case 'spacer':
        return (
          <Box
            key={index}
            display="flex"
            flexDirection="column"
            rowGap={2}
            alignItems="flex-start"
            height={field.value}
          ></Box>
        );

      case 'productAttributes':
        return (
          <Box display={'flex'} flexDirection={'column'}>
            {field.attributes?.map((attrib, attrIndex) => {
              // Find the selected attribute value consistently
              const selectedAttr = field.selectedAttributes?.find(
                (attr) => attr.productAttributeId === attrib.id
              );

              // Get the ID of the selected attribute value
              const selectedValueId =
                selectedAttr?.productAttributeValueId || '';

              return (
                <React.Fragment key={`attrib-${attrIndex}`}>
                  <InputLabel id={`label-${attrib.id}`}>
                    {attrib.attribute.name}
                  </InputLabel>
                  <Select
                    onChange={(e) => field.onChange(e, attrib.id)}
                    labelId={`label-${attrib.id}`}
                    id={`select-${attrib.id}`}
                    fullWidth={true}
                    value={selectedValueId}
                  >
                    {attrib?.productAttributeValues?.map(
                      (option, menuIndex) => (
                        <MenuItem key={`menu-${menuIndex}`} value={option.id}>
                          {option.attributeValue.value}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  <br />
                </React.Fragment>
              );
            })}
          </Box>
        );
      case 'select':
        return (
          <>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id={`dropdown-label-${index}`}>
                {field.label}
              </InputLabel>
              <Select
                onChange={field.onChange}
                labelId={`dropdown-label-${index}`}
                id={`dropdown-${index}`}
                label={field.label}
                fullWidth={true}
                value={field.value || ''}
                key={index}
              >
                {field.selectOptions?.map((option, optIndex) => (
                  <MenuItem key={`option-${optIndex}`} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        );

      case 'selector':
        return (
          <PrizetAttributeSelector
            key={index}
            onAttributeEvent={field.onAttributeEvent}
            label={field.label}
            name={field.name}
            value={field.value as unknown as any[]}
            options={field.options}
          />
        );

      case 'checkbox':
        return (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={!!field.value}
                value={field.value}
                onChange={field.onChange}
                inputProps={{
                  'aria-label': field.label
                }}
              />
            }
            label={field.label}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={index}
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            name={field.name}
            required={field.required}
            disabled={field.disabled}
            onChange={field.onChange}
            value={field.value || ''}
            fullWidth={true}
            multiline
            rows={4}
          />
        );

      case 'text':
        return (
          <TextField
            key={index}
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            name={field.name}
            required={field.required}
            disabled={field.disabled}
            onChange={field.onChange}
            value={field.value || ''}
            style={{ width: '350px' }}
          />
        );

      case 'number':
        return (
          <TextField
            key={index}
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            name={field.name}
            required={field.required}
            disabled={field.disabled}
            onChange={field.onChange}
            value={field.value || ''}
            style={{ width: '350px' }}
          />
        );
    }
  };

  const onSubmit = () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      // Use the enhanced field collection method
      const fieldValues = collectFieldValues(fields);

      // Add additional payload
      const finalPayload = {
        ...props.additionalPayload,
        ...fieldValues
      };

      // Ensure required arrays are defined
      // if (!finalPayload.states) finalPayload.states = [];
      if (baseUrl.includes("variants") || baseUrl.includes("products")) {
        if (!finalPayload.attributes) finalPayload.attributes = [];
        if (!finalPayload.tags) finalPayload.tags = [];
        if (!finalPayload.images) finalPayload.images = [];
        if (!finalPayload.additionalInfo) finalPayload.additionalInfo = [];
      }

      let hasError = false;

      enhancedFetch(`${baseUrl}`, {
        method,
        headers,
        body: JSON.stringify(finalPayload)
      })
        .then((response) => {
          onSubmitCallback(response);
        })
        .catch((error) => {
          hasError = true;
          console.error('Error occurred:', error);

          // Personalized error messages
          let errorMsg = 'An error occurred. Please try again.';

          if (error.response && error.response.status) {
            // Handle different error status codes
            switch (error.response.status) {
              case 400:
                errorMsg =
                  'The form contains invalid data. Please check your inputs.';
                break;
              case 401:
                errorMsg = 'Your session has expired. Please log in again.';
                break;
              // ... other error cases
              default:
                errorMsg =
                  error.response.data?.message ||
                  'An unexpected error occurred. Please try again.';
            }
          } else if (error.message) {
            if (error.message.includes('Network Error')) {
              errorMsg =
                'Network error. Please check your internet connection.';
            } else if (error.message.includes('timeout')) {
              errorMsg = 'Request timed out. Please try again.';
            } else {
              errorMsg = error.message;
            }
          }

          onErrorCallback(error);
          toast.error(errorMsg);
          setErrorMessage(errorMsg);
        })
        .finally(() => {
          setIsLoading(false);
          if (!hasError) {
            onClose();
          }
        });
    } catch (error) {
      console.error('Error preparing payload:', error);
      setIsLoading(false);
      setErrorMessage('Error processing form data.');
      toast.error('Error processing form data.');
    }
  };

  return (
    <Dialog
      PaperProps={{
        style: { zIndex: 1300 } // Higher z-index
      }}
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={openModal}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        {title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        {errorMessage && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}
        {fields.map((field: HttpFieldsProps | HttpLayoutProps, index: number) =>
          getFieldFromType(field, index)
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} autoFocus onClick={onSubmit}>
          {isLoading ? (
            <CircularProgress size={16} />
          ) : submitText?.length ? (
            submitText
          ) : (
            'Submit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
